// -----------------------------------------------------------------------------
// Adaptive Sampling — dark theme tokens.
//
// Palette, font stack, and Plotly colourscales are lifted verbatim from
//   mf_results/article_internal.html               (CSS :root variables)
//   thread-api/app/presentations/adaptive-sampling/ (Plotly layouts)
//
// The only permitted vocabulary is: sampler, next batch, uncertainty,
// mean estimate, surrogate, score. No algorithm names appear here.
// -----------------------------------------------------------------------------

export type AdaptiveSamplingTheme = {
  /** Page background (article --bg). */
  background: string;
  /** Card / panel background (article --bg-card). */
  panel: string;
  /** Elevated panel background (article --bg-elevated). */
  panelElevated: string;
  /** Body text colour (article --text). */
  text: string;
  /** Muted text / axis labels (article --text-muted). */
  textMuted: string;
  /** Dim text / captions (article --text-dim). */
  textDim: string;
  /** Ridge-highlight pink (lifted from triptych LHS colorscale top). */
  pink: string;
  /** Deeper pink tier, for gradient stops. */
  pinkDeep: string;
  /** Thread cyan (article --accent). */
  accent: string;
  /** Darker cyan for gridlines / underlays. */
  accentDim: string;
  /** Grid colour on Plotly axes. */
  grid: string;
  /** Diverging colourscale: PiYG, ridge side pink. Array of Plotly stops. */
  divergingPalette: string[];
  /** Sequential colourscale for mean / uncertainty. Plasma stops. */
  sequentialPalette: string[];
  /** CSS font-family stack. */
  fontStack: string;
  /** Monospace stack for axis tick labels, chip labels. */
  monoStack: string;
  /** Serif stack for display titles. */
  serifStack: string;
};

/**
 * Sequential plasma colourscale — the default for mean-estimate heatmaps
 * and 3D surfaces. Lifted from the shared Plotly template baked into
 * `presentations/adaptive-sampling/x57_stall_boundary_comparison.html`.
 */
const SEQUENTIAL_PLASMA: string[] = [
  '#0d0887',
  '#46039f',
  '#7201a8',
  '#9c179e',
  '#bd3786',
  '#d8576b',
  '#ed7953',
  '#fb9f3a',
  '#fdca26',
  '#f0f921',
];

/**
 * Diverging PiYG — centred at #f7f7f7, pink on the negative side so that
 * Truth − Estimate errors highlight with the same ridge-pink we use for
 * the sampler-vs-truth story. Lifted from the same Plotly template.
 */
const DIVERGING_PIYG: string[] = [
  '#8e0152',
  '#c51b7d',
  '#de77ae',
  '#f1b6da',
  '#fde0ef',
  '#f7f7f7',
  '#e6f5d0',
  '#b8e186',
  '#7fbc41',
  '#4d9221',
  '#276419',
];

export const adaptiveSamplingTheme: AdaptiveSamplingTheme = {
  background:        '#0a0a0a',
  panel:             '#111316',
  panelElevated:     '#161a1e',
  text:              '#e0e0e0',
  textMuted:         '#8a8f98',
  textDim:           '#555a63',
  pink:              '#ff4080',
  pinkDeep:          '#cc4488',
  accent:            '#00d4ff',
  accentDim:         '#007a94',
  grid:              '#1e2228',
  divergingPalette:  DIVERGING_PIYG,
  sequentialPalette: SEQUENTIAL_PLASMA,
  fontStack:
    "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  monoStack: "'JetBrains Mono', 'SFMono-Regular', Menlo, monospace",
  serifStack: "'DM Serif Display', 'Times New Roman', serif",
};

// ---------------------------------------------------------------------------
// Plotly layout helpers — produce a base layout object every chart component
// merges over. Keeps paper/plot backgrounds, fonts, grid colours consistent.
// ---------------------------------------------------------------------------

export type PlotlyLayoutPartial = Record<string, unknown>;

/**
 * Plotly plot-area margins shared by the adaptive-sampling chart primitives.
 * The plotting rect (where data is drawn) is the container inset by these
 * margins; anything overlaid on the chart (e.g. PointOverlay) must inset by
 * the same amounts to line up with the heatmap beneath it (FXC-9003).
 */
export const PLOTLY_PLOT_MARGIN = { t: 24, b: 44, l: 56, r: 20 } as const;

/**
 * Build the shared Plotly base layout. All chart primitives spread this
 * object and then override the bits they need.
 */
export function basePlotlyLayout(
  overrides: PlotlyLayoutPartial = {},
): PlotlyLayoutPartial {
  const t = adaptiveSamplingTheme;
  return {
    paper_bgcolor: t.background,
    plot_bgcolor: t.panel,
    font: {
      family: 'Outfit, sans-serif',
      color: t.text,
      size: 12,
    },
    margin: { ...PLOTLY_PLOT_MARGIN },
    colorway: [
      t.accent,
      t.pink,
      '#fdca26',
      '#7fbc41',
      '#ab63fa',
      '#FF9F43',
    ],
    xaxis: {
      gridcolor: t.grid,
      zerolinecolor: t.grid,
      linecolor: t.textDim,
      color: t.textMuted,
      tickfont: { color: t.textMuted, size: 11 },
      title: { standoff: 12 },
    },
    yaxis: {
      gridcolor: t.grid,
      zerolinecolor: t.grid,
      linecolor: t.textDim,
      color: t.textMuted,
      tickfont: { color: t.textMuted, size: 11 },
      title: { standoff: 12 },
    },
    hoverlabel: {
      bgcolor: t.panelElevated,
      bordercolor: t.accent,
      font: { family: 'JetBrains Mono, monospace', color: t.text, size: 11 },
    },
    legend: {
      bgcolor: 'rgba(10,10,10,0.8)',
      bordercolor: 'rgba(255,255,255,0.06)',
      borderwidth: 1,
      font: { color: t.text, size: 11 },
    },
    ...overrides,
  };
}

/**
 * Build a Plotly colorscale array of [stop, colour] pairs from a palette
 * of colour strings. Stops are equally spaced on [0, 1].
 */
export function paletteToColorscale(
  palette: string[],
): Array<[number, string]> {
  const n = palette.length;
  if (n === 0) return [];
  if (n === 1) {
    const only = palette[0] as string;
    return [
      [0, only],
      [1, only],
    ];
  }
  return palette.map((c, i) => [i / (n - 1), c] as [number, string]);
}

/**
 * Scene config for 3D plots — matches the dark backgrounds used in
 * x57_3d_surface_triptych.html.
 */
export function basePlotly3DScene(): PlotlyLayoutPartial {
  const t = adaptiveSamplingTheme;
  const axis = {
    backgroundcolor: t.panel,
    gridcolor: t.grid,
    gridwidth: 1,
    linecolor: t.textDim,
    showbackground: true,
    zerolinecolor: t.grid,
    color: t.textMuted,
    tickfont: { color: t.textMuted, size: 10 },
    title: { font: { color: t.textMuted, size: 12 } },
  };
  return {
    xaxis: axis,
    yaxis: axis,
    zaxis: axis,
    bgcolor: t.background,
    camera: {
      eye: { x: 1.5, y: 1.5, z: 1.1 },
      up: { x: 0, y: 0, z: 1 },
    },
    aspectmode: 'cube',
  };
}
